import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import { emphasize } from '@mui/material/styles';
import { withStyles } from '@mui/styles';
import {
    Tooltip,
    MenuItem,
    Paper,
    TextField,
    Typography,
    IconButton,
    Chip,
    FormHelperText
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { compose } from 'redux';
import { COLORS } from '../../../utils/constants';

const checkIsValueTruncated = el => (el && el.offsetWidth < el.scrollWidth);

const styles = theme => ({
    root: {
        flexGrow: 1
    },
    input: {
        display: 'flex',
        padding: '6px 0 0',
        height: 'auto'
    },
    valueContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        flex: 1,
        alignItems: 'center',
        overflow: 'hidden'
    },
    chip: {
        maxWidth: '98%',
        margin: `${theme.spacing(0.5)} ${theme.spacing(0.25)}`,
        fontWeight: 400,
        color: '#000'
    },
    chipFocused: {
        backgroundColor: emphasize(
            theme.palette.mode === 'light' ? theme.palette.grey[300] : theme.palette.grey[700],
            0.08
        )
    },
    optionValue: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    noOptionsMessage: {
        padding: `${theme.spacing(1)} ${theme.spacing(2)}`
    },
    singleValue: {
        fontSize: 16
    },
    placeholder: {
        position: 'absolute',
        left: 2,
        fontSize: 16
    },
    paper: {
        position: 'absolute',
        zIndex: 1,
        marginTop: theme.spacing(1),
        left: 0,
        right: 0
    },
    divider: {
        height: theme.spacing(2)
    }
});

const DEBOUNCE_TIME = 300;

function NoOptionsMessage(props) {
    return (
        <Typography
            color="textSecondary"
            className={props.selectProps.classes.noOptionsMessage}
            {...props.innerProps}
        >
            {props.children}
        </Typography>
    );
}

NoOptionsMessage.propTypes = {
    selectProps: PropTypes.shape({
        classes: PropTypes.shape({
            noOptionsMessage: PropTypes.string,
        }),
    }),
    innerProps: PropTypes.object,
    children: PropTypes.node
};

const inputComponent = React.forwardRef((props, ref) => (
    <div
        ref={ref}
        {...props}
    />
)
);

inputComponent.propTypes = {
    inputRef: PropTypes.oneOfType([
        PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
        PropTypes.func,
    ])
};

function Control(props) {
    return (
        <TextField
            variant="standard"
            required
            fullWidth
            error={!!props.selectProps.error}
            InputProps={{
                inputComponent,
                inputProps: {
                    className: props.selectProps.classes.input,
                    ref: props.innerRef,
                    children: props.children,
                    required: true,
                    ...props.innerProps
                }
            }}
            {...props.selectProps.textFieldProps}
        />
    );
}

Control.propTypes = {
    selectProps: PropTypes.shape({
        textFieldProps: PropTypes.object,
        error: PropTypes.string,
        classes: PropTypes.shape({
            input: PropTypes.string,
        }),
    }),
    innerRef: PropTypes.oneOfType([
        PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
        PropTypes.func,
    ]),
    innerProps: PropTypes.object,
    children: PropTypes.node
};

function Option(props) {
    const optionRef = useRef(null);
    const [isValueTruncated, setIsValueTruncated] = useState(false);

    useEffect(() => {
        const span = optionRef?.current;
        setIsValueTruncated(checkIsValueTruncated(span));
    }, []);

    const optionItem = (
        <MenuItem
            ref={props.innerRef}
            selected={props.isFocused}
            disabled={props.children.props ? props.children?.props.disabled : undefined}
            component="div"
            style={{
                fontWeight: props.isSelected ? 600 : 400,
                backgroundColor: props.isDisabled ? COLORS.disabled.backgroundColor : 'inherit',
                color: props.isDisabled ? COLORS.disabled.color : 'inherit'
            }}
            {...props.innerProps}
        >
            <span
                ref={optionRef}
                className={props.selectProps.classes.optionValue}
            >
                {props.children}
            </span>
        </MenuItem>
    );

    if (isValueTruncated) {
        return (
            <Tooltip
                title={props.children}
                placement={props.placement}
            >
                {optionItem}
            </Tooltip>
        );
    }

    return optionItem;
}

Option.propTypes = {
    selectProps: PropTypes.shape({
        classes: PropTypes.shape({
            optionValue: PropTypes.string,
        }),
    }),
    isFocused: PropTypes.bool,
    isSelected: PropTypes.bool,
    innerProps: PropTypes.object,
    children: PropTypes.node,
    innerRef: PropTypes.oneOfType([
        PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
        PropTypes.func,
    ]),
};

function Placeholder(props) {
    return (
        <Typography
            color="textSecondary"
            className={props.selectProps.classes.placeholder}
            {...props.innerProps}
        >
            {props.children}
        </Typography>
    );
}

Placeholder.propTypes = {
    selectProps: PropTypes.shape({
        classes: PropTypes.shape({
            placeholder: PropTypes.string,
        }),
    }),
    innerProps: PropTypes.object,
    children: PropTypes.node,
};

function SingleValue(props) {
    return (
        <Typography
            className={props.selectProps.classes.singleValue}
            {...props.innerProps}
        >
            {props.children}
        </Typography>
    );
}

SingleValue.propTypes = {
    selectProps: PropTypes.shape({
        classes: PropTypes.shape({
            singleValue: PropTypes.string,
        }),
    }),
    innerProps: PropTypes.object,
    children: PropTypes.node,
};

function ValueContainer(props) {
    return <div className={props.selectProps.classes.valueContainer}>{props.children}</div>;
}

ValueContainer.propTypes = {
    selectProps: PropTypes.shape({
        classes: PropTypes.shape({
            valueContainer: PropTypes.string,
        }),
    }),
    children: PropTypes.node,
};

function MultiValue({ placement = 'bottom', ...props }) {
    const chipRef = useRef(null);
    const [isValueTruncated, setIsValueTruncated] = useState(false);

    useEffect(() => {
        const div = chipRef?.current?.children[0];
        setIsValueTruncated(checkIsValueTruncated(div));
    }, []);

    const chipItem = (
        <Chip
            ref={chipRef}
            tabIndex={-1}
            label={props.children}
            className={classNames(props.selectProps.classes.chip, {
                [props.selectProps.classes.chipFocused]: props.isFocused
            })}
            onDelete={props.removeProps.onClick}
        />
    );

    if (isValueTruncated) {
        return (
            <Tooltip
                title={props.children}
                placement={props.placement}
            >
                {chipItem}
            </Tooltip>
        );
    }

    return chipItem;
}

MultiValue.propTypes = {
    selectProps: PropTypes.shape({
        classes: PropTypes.shape({
            chip: PropTypes.string,
            chipFocused: PropTypes.string,
        }),
    }),
    removeProps: PropTypes.shape({
        onClick: PropTypes.func,
    }),
    children: PropTypes.node,
    isFocused: PropTypes.bool,
    placement: PropTypes.string,
};

function Menu(props) {
    return (
        <Paper
            square
            className={props.selectProps.classes.paper}
            {...props.innerProps}
        >
            {props.children}
        </Paper>
    );
}

Menu.propTypes = {
    selectProps: PropTypes.shape({
        classes: PropTypes.shape({
            paper: PropTypes.string,
        }),
    }),
    innerProps: PropTypes.object,
    children: PropTypes.node,
};

const ClearIndicator = props => {
    const {
        innerProps: { ref, ...restInnerProps }
    } = props;
    return (
        <div
            {...restInnerProps}
            ref={ref}
        >
            <IconButton
                aria-label="delete"
                size="small"
            >
                <DeleteIcon />
            </IconButton>
        </div>
    );
};

ClearIndicator.propTypes = {
    selectProps: PropTypes.shape({
        classes: PropTypes.shape({
            paper: PropTypes.string,
            chipFocused: PropTypes.string,
        }),
    }),
    innerProps: PropTypes.shape({
        ref: PropTypes.oneOfType([
            PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
            PropTypes.func,
        ]),
    }),
};

const components = {
    Control,
    Menu,
    MultiValue,
    NoOptionsMessage,
    Option,
    Placeholder,
    SingleValue,
    ValueContainer,
    ClearIndicator
};

class MultiTypeahead extends React.Component {
    constructor(props) {
        super(props);
        this.onChange = debounce(this.onChange, DEBOUNCE_TIME);

        this.state = {
            onChangeFocus: false
        };

        this.selectRef = React.createRef();
    }

    componentDidMount() {
        this._isMounted = true;
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    componentDidUpdate() {
        if (this.state.onChangeFocus) {
            this.selectRef.current?.controlRef?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
                inline: 'nearest'
            });
        }
    }

    handleChange = (value, { action }) => {
        if (action === 'remove-value' && this.props.titleId === 'assignTo') {
            this.selectRef.current.blur();
        }
        this.props.onChangeSuggestion(value);
    };

    onChange = value => {
        if (this.props.handleSuggestionsFetchRequested) {
            this.props.handleSuggestionsFetchRequested(value);
        }
    };

    render() {
        const {
            classes,
            theme,
            titleId,
            isClearable,
            isRequired,
            isLoading,
            backspaceRemovesValue,
            animatedPlaceholder,
            options,
            isMulti,
            handleSuggestionsClearRequested,
            error,
            selectFilterOption,
            formatGroupLabel,
            formatOptionLabel,
            value,
            onFocus,
            disabled,
            noLabel,
            required = true,
            t
        } = this.props;

        const { onChangeFocus } = this.state;

        const selectStyles = {
            clearIndicator: base => ({
                ...base,
                padding: 0,
                ':hover': {
                    cursor: 'pointer'
                }
            }),
            input: base => ({
                ...base,
                color: theme.palette.text.primary,
                '& input': {
                    font: 'inherit'
                }
            }),
            indicatorSeparator: base => ({
                ...base,
                display: 'none'
            }),
            dropdownIndicator: base => ({
                ...base,
                display: 'none'
            })
        };

        let blurTimeOut;

        return (
            <div className={classes.root}>
                {disabled ? (
                    <TextField
                        className={classes.input}
                        disabled
                        required={required}
                        label={noLabel ? null : t(titleId)}
                        value={value?.label || ''}
                        variant="standard"
                    />
                ) : (
                    <Select
                        variant="standard"
                        ref={this.selectRef}
                        options={options}
                        getOptionValue={option => option.label}
                        onBlur={handleSuggestionsClearRequested}
                        classes={classes}
                        styles={selectStyles}
                        textFieldProps={{
                            label: noLabel ? null : t(titleId),
                            required: isRequired,
                            InputLabelProps:
                                !isEmpty(value) || onChangeFocus || !animatedPlaceholder
                                    ? {
                                        filled: true,
                                        shrink: true
                                    }
                                    : {
                                        filled: false,
                                        shrink: false
                                    },
                            onBlur: v => {
                                if (this._isMounted) {
                                    this.setState({ onChangeFocus: false });
                                }
                                return v;
                            },
                            onFocus: v => {
                                if (onFocus) {
                                    onFocus();
                                }

                                this.setState({ onChangeFocus: true }, () => {
                                    if (blurTimeOut) {
                                        clearTimeout(blurTimeOut);
                                        blurTimeOut = undefined;
                                    }
                                });
                                return v;
                            }
                        }}
                        components={components}
                        onInputChange={this.onChange}
                        onChange={this.handleChange}
                        value={value}
                        placeholder={animatedPlaceholder || noLabel ? null : t(titleId)}
                        isMulti={isMulti}
                        isClearable={isClearable}
                        backspaceRemovesValue={backspaceRemovesValue}
                        error={error}
                        menuPosition="relative"
                        filterOption={selectFilterOption}
                        formatGroupLabel={formatGroupLabel}
                        formatOptionLabel={formatOptionLabel}
                        isLoading={isLoading}
                        aria-label="Select"
                    />
                )}
                {error && (
                    <FormHelperText
                        error={!!error}
                        variant="standard"
                    >
                        {t(error)}
                    </FormHelperText>
                )}
            </div>
        );
    }
}

MultiTypeahead.propTypes = {
    classes: PropTypes.object.isRequired,
    theme: PropTypes.object.isRequired,
    selectFilterOption: PropTypes.func,
    isLoading: PropTypes.bool,
    onFocus: PropTypes.func,
    titleId: PropTypes.string,
    isClearable: PropTypes.bool,
    isRequired: PropTypes.bool,
    backspaceRemovesValue: PropTypes.bool,
    animatedPlaceholder: PropTypes.bool,
    options: PropTypes.array,
    isMulti: PropTypes.bool,
    handleSuggestionsClearRequested: PropTypes.func,
    error: PropTypes.string,
    formatGroupLabel: PropTypes.func,
    formatOptionLabel: PropTypes.func,
    onChangeSuggestion: PropTypes.func,
    handleSuggestionsFetchRequested: PropTypes.func,
    disabled: PropTypes.bool,
    required: PropTypes.bool,
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
        PropTypes.number,
        PropTypes.array,
        PropTypes.shape({
            label: PropTypes.string,
            value: PropTypes.string,
        }),
    ]),
};

export default compose(
    withStyles(styles, { withTheme: true }))(MultiTypeahead);
